import { useInjection } from 'inversify-react';
import { useCallback, useRef } from 'react';

import { VideoManager } from '../../../../core/viewer/video-manager';
import { TYPES } from '../../../../inversify.types';

export interface UseVideoEventListenersParams {
  pageId: number;
}

export interface EventHandlers {
  endedHandler: (event: Event) => void;
  playHandler: (event: Event) => void;
  pauseHandler: (event: Event) => void;
  clickHandler: (event: Event) => void;
  timeUpdateHandler: (event: Event) => void;
}

export function useVideoEventListeners({ pageId }: UseVideoEventListenersParams) {
  const videoManager = useInjection<VideoManager>(TYPES.Viewer.VideoManager);
  const containerElementRef = useRef<HTMLDivElement | null>(null);
  const handlersRef = useRef<EventHandlers | null>(null);

  const callbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (containerElementRef.current && handlersRef.current) {
        const element = containerElementRef.current;
        const handlers = handlersRef.current;

        element.removeEventListener('ended', handlers.endedHandler, true);
        element.removeEventListener('play', handlers.playHandler, true);
        element.removeEventListener('pause', handlers.pauseHandler, true);
        element.removeEventListener('click', handlers.clickHandler, true);
        element.removeEventListener('timeupdate', handlers.timeUpdateHandler, true);

        handlersRef.current = null;
      }

      containerElementRef.current = node;

      if (!node) return;

      const handlers: EventHandlers = {
        endedHandler: videoManager.videoEndedHandler(pageId),
        playHandler: videoManager.videoPlayPauseHandler('play', pageId),
        pauseHandler: videoManager.videoPlayPauseHandler('pause', pageId),
        clickHandler: videoManager.handleVideoButtonClick(pageId),
        timeUpdateHandler: videoManager.handleVideoTimeUpdate(pageId),
      };

      handlersRef.current = handlers;

      // Attach event listeners
      node.addEventListener('ended', handlers.endedHandler, true);
      node.addEventListener('play', handlers.playHandler, true);
      node.addEventListener('pause', handlers.pauseHandler, true);
      node.addEventListener('click', handlers.clickHandler, true);
      node.addEventListener('timeupdate', handlers.timeUpdateHandler, true);
    },
    [videoManager, pageId]
  );

  return { callbackRef, containerRef: containerElementRef };
}
